import {
  Component,
  OnDestroy,
  OnInit,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { Subject, takeUntil } from 'rxjs';
import { TutorialService } from '../tutorial.service';
import { TutorialState, TutorialStep } from '../tutorial.model';

interface PaneStyle {
  top: string;
  left: string;
  width: string;
  height: string;
}

interface CardPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
}

/**
 * TutorialOverlayComponent
 *
 * Renders at the app-layout level. It is invisible when no tour is active.
 * When a tour is active it shows:
 *  - Four semi-transparent backdrop panes arranged around the highlighted element
 *  - A bright outline ring on the highlighted element
 *  - A floating step card with Previous / Next (or Done) / Skip controls
 */
@Component({
  selector: 'app-tutorial-overlay',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule],
  templateUrl: './tutorial-overlay.component.html',
  styleUrls: ['./tutorial-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TutorialOverlayComponent implements OnInit, OnDestroy {
  state: TutorialState | null = null;

  /** The live bounding rect of the currently targeted element. */
  targetRect: DOMRect | null = null;

  /** Computed position for the floating step card. */
  cardPosition: CardPosition = {};

  /** The element currently highlighted (kept so we can remove the CSS class on step change). */
  private highlightedEl: Element | null = null;

  private readonly destroy$ = new Subject<void>();

  /** How many px of padding to add around the spotlight ring. */
  private readonly RING_PADDING = 6;

  /** Gap between the spotlight ring and the step card. */
  private readonly CARD_GAP = 16;

  constructor(
    private readonly tutorialService: TutorialService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.tutorialService.tutorialState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.state = state;
        this.updateSpotlight();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.clearHighlight();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Window resize recalculation ─────────────────────────────────────────────

  @HostListener('window:resize')
  onResize(): void {
    if (this.state?.isActive) {
      this.updateSpotlight();
      this.cdr.markForCheck();
    }
  }

  // ── Template helpers ────────────────────────────────────────────────────────

  get currentStep(): TutorialStep | undefined {
    if (!this.state) {return undefined;}
    return this.state.steps[this.state.currentStepIndex];
  }

  get isFirstStep(): boolean {
    return (this.state?.currentStepIndex ?? 0) === 0;
  }

  get isLastStep(): boolean {
    if (!this.state) {return false;}
    return this.state.currentStepIndex === this.state.steps.length - 1;
  }

  get stepLabel(): string {
    if (!this.state) {return '';}
    return `Step ${this.state.currentStepIndex + 1} of ${this.state.steps.length}`;
  }

  // ── Pane geometry ───────────────────────────────────────────────────────────

  /** Top pane: full width, from viewport top to top of the spotlight ring. */
  get topPaneStyle(): PaneStyle {
    const r = this.ringedRect;
    return {
      top: '0',
      left: '0',
      width: '100vw',
      height: `${Math.max(0, r.top)}px`,
    };
  }

  /** Left pane: from bottom of top pane to bottom of spotlight, left side. */
  get leftPaneStyle(): PaneStyle {
    const r = this.ringedRect;
    const top = Math.max(0, r.top);
    return {
      top: `${top}px`,
      left: '0',
      width: `${Math.max(0, r.left)}px`,
      height: `${Math.max(0, r.height)}px`,
    };
  }

  /** Right pane: symmetric to left. */
  get rightPaneStyle(): PaneStyle {
    const r = this.ringedRect;
    const top = Math.max(0, r.top);
    const rightStart = r.left + r.width;
    return {
      top: `${top}px`,
      left: `${rightStart}px`,
      width: `${Math.max(0, window.innerWidth - rightStart)}px`,
      height: `${Math.max(0, r.height)}px`,
    };
  }

  /** Bottom pane: full width, from bottom of spotlight to bottom of viewport. */
  get bottomPaneStyle(): PaneStyle {
    const r = this.ringedRect;
    const bottomStart = r.top + r.height;
    return {
      top: `${bottomStart}px`,
      left: '0',
      width: '100vw',
      height: `${Math.max(0, window.innerHeight - bottomStart)}px`,
    };
  }

  /** The spotlight ring sits on top of the panes and outlines the target. */
  get spotlightRingStyle(): Partial<CSSStyleDeclaration> {
    const r = this.ringedRect;
    return {
      top: `${r.top}px`,
      left: `${r.left}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
    } as unknown as Partial<CSSStyleDeclaration>;
  }

  // ── User actions ────────────────────────────────────────────────────────────

  next(): void {
    this.tutorialService.next();
  }

  prev(): void {
    this.tutorialService.prev();
  }

  skip(): void {
    this.tutorialService.skip();
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  /**
   * Finds the target element for the current step, scrolls it into view,
   * applies the highlight CSS class, and computes the card position.
   */
  private updateSpotlight(): void {
    this.clearHighlight();

    if (!this.state?.isActive || !this.currentStep) {
      this.targetRect = null;
      return;
    }

    const el = document.querySelector(this.currentStep.target);
    if (!el) {
      console.warn(
        `[TutorialOverlay] Could not find element for selector: "${this.currentStep.target}"`,
      );
      this.targetRect = null;
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Wait for scroll to settle before computing rect.
    setTimeout(() => {
      this.targetRect = el.getBoundingClientRect();
      this.cardPosition = this.computeCardPosition(
        this.targetRect,
        this.currentStep?.position ?? 'bottom',
      );
      this.highlightedEl = el;
      this.cdr.markForCheck();
    }, 150);
  }

  private clearHighlight(): void {
    this.highlightedEl = null;
  }

  /**
   * Returns the rect of the spotlight ring (target rect expanded by RING_PADDING).
   * Falls back to a zero-size rect in the centre of the viewport when no target is resolved.
   */
  private get ringedRect(): DOMRect {
    if (!this.targetRect) {
      return new DOMRect(
        window.innerWidth / 2,
        window.innerHeight / 2,
        0,
        0,
      );
    }
    return new DOMRect(
      this.targetRect.left - this.RING_PADDING,
      this.targetRect.top - this.RING_PADDING,
      this.targetRect.width + this.RING_PADDING * 2,
      this.targetRect.height + this.RING_PADDING * 2,
    );
  }

  /**
   * Computes the position of the step card so that it appears adjacent to the
   * spotlight ring.  If the preferred side would overflow the viewport, the
   * card is flipped to the opposite side automatically.
   */
  private computeCardPosition(
    rect: DOMRect,
    preferred: TutorialStep['position'],
  ): CardPosition {
    const cardWidth = 320;
    const cardHeight = 180; // approximate
    const gap = this.CARD_GAP + this.RING_PADDING;

    // Centre card horizontally on the target for top/bottom placements.
    let centreLeft = rect.left + rect.width / 2 - cardWidth / 2;
    centreLeft = Math.max(8, Math.min(centreLeft, window.innerWidth - cardWidth - 8));

    const belowTop = rect.bottom + gap;
    const aboveBottom = window.innerHeight - rect.top + gap;
    const rightLeft = rect.right + gap;
    const leftRight = window.innerWidth - rect.left + gap;

    // Determine if each side has enough space.
    const fitsBelow = belowTop + cardHeight < window.innerHeight;
    const fitsAbove = rect.top - gap - cardHeight > 0;
    const fitsRight = rect.right + gap + cardWidth < window.innerWidth;
    const fitsLeft  = rect.left - gap - cardWidth > 0;

    let side = preferred ?? 'bottom';

    // Flip if preferred side doesn't fit.
    if (side === 'bottom' && !fitsBelow && fitsAbove) {side = 'top';}
    if (side === 'top'    && !fitsAbove && fitsBelow) {side = 'bottom';}
    if (side === 'right'  && !fitsRight && fitsLeft)  {side = 'left';}
    if (side === 'left'   && !fitsLeft  && fitsRight) {side = 'right';}

    switch (side) {
      case 'top':
        return { bottom: `${aboveBottom}px`, left: `${centreLeft}px` };
      case 'left':
        return { top: `${rect.top}px`, right: `${leftRight}px` };
      case 'right':
        return { top: `${rect.top}px`, left: `${rightLeft}px` };
      case 'bottom':
      default:
        return { top: `${belowTop}px`, left: `${centreLeft}px` };
    }
  }
}
